
import { scaleBand, scaleLinear, format } from 'd3';
import * as d3 from 'd3';
import numberWithCommas from '../../utils/numberWithCommas';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';


const margin = { top: 20, right: 0, bottom: 30, left: 60 };
const siFormat = format('.2s');
// Formats used to style different numbers and values. Will be changed/removed when live data is used
const yAxisTickFormat = (tickValue: number) => tickValue < 1 ? tickValue : siFormat(tickValue).replace('G', 'B');
const xAxisTickFormat = (tickValue: string) => { return tickValue.replace("United States of America", "USA"); };
const hoverColor = "#eec42d";
const staticColor = '#437c90';

interface AxisBottomProps {
	xScale: any;
	innerHeight: number;
	tickFormat: any;
}

interface AxisLeftProps {
	yScale: any;
	tickFormat: any;
	innerWidth: number;
	leftAxis: any;

}

interface MarksProps {
	data: any;
	xScale: any;
	yScale: any;
	xValue: any;
	yValue: any;
	innerWidth: number;
	innerHeight: number;
}

interface BarChartAudienceRetentionProps {
	width: number;
	height: number;
	dataIn: any;
	loading?: boolean,
	error?: string | null
}

// Creates the elements for the bottom axis
const AxisBottom = ({ xScale, innerHeight, tickFormat }: AxisBottomProps) =>
	xScale.domain().map((tickValue: number) => (
		<g
			className="barchart-tick"
			key={tickValue}
		>
			<text
				x={xScale(tickValue) +
					xScale.bandwidth() / 2}
				y={innerHeight + 6 + margin.bottom / 2}
				style={{ textAnchor: "middle" }}
			>
				{tickFormat(tickValue)}
			</text>
		</g>
	));


// Creates the elements for the left axis
const AxisLeft = ({ yScale, tickFormat, innerWidth, leftAxis }: AxisLeftProps) =>
	leftAxis.map((tickValue: number) => (
		<g
			className="barchart-tick"
			key={tickValue}
			transform={`translate(0,${yScale(tickValue)})`}
		>
			<line
				x2={innerWidth}
			/>

			<text
				x={-10}
				// y={yScale(tickValue) +
				//     yScale.bandwidth() / 2}
				style={{ textAnchor: 'end' }}
			>
				{tickFormat(tickValue)}
			</text>
		</g>

	));

// Creates the bars for the retention chart using svgs
const Marks = ({ data, xScale, yScale, xValue, yValue, innerHeight }: MarksProps) => {
	const tooltip = d3
		.select('#retention-chart-container')
		.append('div')
		.attr('class', 'd3-tooltip-retention')
		.attr('id', 'd3-tooltip-retention')
		.style('position', 'absolute')
		.style('z-index', '10')
		.style('visibility', 'hidden')
		.style('padding', '10px')
		.style('background', 'rgba(0,0,0,0.6)')
		.style('border-radius', '4px')
		.style('color', '#fff')
		.text('a simple tooltip');
	return (data.map((d: any) => (
		<g key={`${xValue(d)}`}>
			<rect
				className={"barchart-mark"}
				x={xScale(xValue(d))}
				y={yScale(yValue(d))}
				width={xScale.bandwidth()}
				height={0}//{innerHeight - yScale(yValue(d))}
				rx={4}
				onMouseEnter={function() {
					tooltip
						.html(`<div>${numberWithCommas(yValue(d).toFixed(0))}</div>`)
						.style('visibility', 'visible');
					d3.select(".d3-tooltip-retention").transition().attr('fill', hoverColor);

				}}

				onMouseMove={function(event: any) {
					tooltip
						.style('top', event.pageY - 10 + 'px')
						.style('left', event.pageX + 10 + 'px');
				}}
				onMouseOut={function() {
					tooltip.html(``).style('visibility', 'hidden');
					d3.select('.d3-tooltip-retention').transition().attr('fill', staticColor);
				}}>

				<animate attributeName="height" from="0" to={innerHeight - yScale(yValue(d))} dur="0.5s" fill="freeze" />
				<animate attributeName="y" from={innerHeight} to={yScale(yValue(d))} dur="0.5s" fill="freeze" />
			</rect>
			<g className={"barchart-mark-percentage"}></g>
		</g>
	)));
};

function BarChartAudienceRetention({ width, height, dataIn, loading, error }: BarChartAudienceRetentionProps): JSX.Element {

	if (error) {
		return <div className="retention-chart-loading">{error}</div>;
	}
	if (loading) {
		return <div className="retention-chart-loading"><WaitingIndicator /></div>;
	}
	if (!dataIn || !dataIn.length) {
		return <div className="retention-chart-loading">No data to display</div>;
	}

	const xValue = (d: any) => d.retention;
	const yValue = (d: any) => parseFloat(d.count);
	const innerHeight = height - margin.top - margin.bottom;
	const innerWidth = width - margin.left - margin.right;

	const xScale = scaleBand()
		.domain(dataIn.map(xValue))
		.range([0, innerWidth])
		.paddingInner(0.4);

	const maxOfData = Math.max(...dataIn.map((o: any) => o.count), 0);

	const yScale = scaleLinear()
		.domain([0, maxOfData])
		.range([innerHeight, 0]);

	const leftAxis = yScale.ticks(5);

	// Piece it all together
	return (
		<div id="retention-chart-container">
			<svg className="barChart-container" width={width} height={height} id={`retention-chart`}>
				<g key={"barChart-container"} transform={`translate(${margin.left},${margin.top})`}>
					<AxisBottom
						xScale={xScale}
						innerHeight={innerHeight}
						tickFormat={xAxisTickFormat} />
					<AxisLeft
						yScale={yScale}
						tickFormat={yAxisTickFormat}
						innerWidth={innerWidth}
						leftAxis={leftAxis}
					/>
					<Marks
						data={dataIn}
						xScale={xScale}
						yScale={yScale}
						xValue={xValue}
						yValue={yValue}
						innerWidth={innerWidth}
						innerHeight={innerHeight}
					/>

				</g>
			</svg>
		</div>
	);
}

export default BarChartAudienceRetention;
